import { ElmLitElement, fireEvent } from '../../ElmLitElement';
import { debounce } from 'debounce';
import { alwaysVoid, observeDom } from '../../util';

const selectedItemAttr = 'selected-item' as const;
const scrollSettledEvent = 'scrollSettled' as const;
const debouncedVibrate = toVibrateEffect();

export {
  defineSlideSelectHorizontalElement,
  defineCustomizedSlideSelectHorizontalElement,
};

const defineCustomizedSlideSelectHorizontalElement = (config: {
  componentName: string,
  elMutator: ElementStyleMutatorOnScroll
}): void =>
  customElements.define(config.componentName, class CustomizedSlideSelectHorizontal extends SlideSelectHorizontal {
    elMutator = config.elMutator
  });

const defineSlideSelectHorizontalElement = (): void =>
  customElements.define('slide-select-horizontal', SlideSelectHorizontal);

class SlideSelectHorizontal extends ElmLitElement {
  disconnectListeners = alwaysVoid;
  elMutator: ElementStyleMutatorOnScroll | undefined;
  
  connectedCallback(): void {
    const scrollerState = toScrollerState(this);
    const scrollToSelectedItemDebounced = debounce(() => scrollToSelectedItemIfNotInteracting(this, scrollerState), 500);
    const fireSelectedDebounced = debounce(handleFiringEvent, 400);
    const markDoneScrollingDebounced = debounce(onDoneScrolling, 400);
    const scrollHandler = toScrollHandler(this, scrollerState, fireSelectedDebounced, scrollToSelectedItemDebounced, markDoneScrollingDebounced, this.elMutator);
    const destroyReInitObservers = updateScrollerStateOnEvents(this, scrollerState);
    this.addEventListener('scroll', scrollHandler);
    this.addEventListener('touchstart', onTouchStart)
    this.addEventListener('touchend', onTouchDone);
    this.addEventListener('touchcancel', onTouchDone);
    const selectedItemObserver = observeDom(this, scrollToSelectedItemDebounced, {subtree: true, attributeFilter: [selectedItemAttr]});
    scrollToSelectedItem(this, true);
    
    this.disconnectListeners = () => {
      scrollToSelectedItemDebounced.clear();
      fireSelectedDebounced.clear();
      destroyReInitObservers();
      this.removeEventListener('scroll', scrollHandler);
      selectedItemObserver.disconnect();
    };
    
    function onTouchStart(): void {
      scrollToSelectedItemDebounced.clear();
      fireSelectedDebounced.clear();
      scrollerState.isTouched = true;
    }
    function onTouchDone(): void {
      scrollerState.isTouched = false;
      onDoneInteracting();
    }
    function onDoneScrolling(): void {
      scrollerState.isScrolling = false;
      onDoneInteracting();
    }
    function onDoneInteracting(): void {
      if (scrollerState.pendingSelectedItem && !scrollerState.isTouched && !scrollerState.isScrolling) {
        fireSelectedItem(scrollerState, handleFiringEvent, scrollerState.pendingSelectedItem);
      }
    }
  }
  
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.disconnectListeners();
  }
}

function scrollToSelectedItemIfNotInteracting(slider: SlideSelectHorizontal, state: ScrollerState): void {
  if (!state.isScrolling && !state.isTouched) {
    scrollToSelectedItem(slider);
  }
}



function toScrollerState(slider: SlideSelectHorizontal): ScrollerState {
  return {
    items: toItemsPlacements(slider),
    width: slider.offsetWidth,
    half: slider.offsetWidth / 2,
    isTouched: false,
    isScrolling: false,
  };
}

function updateScrollerStateOnEvents(slider: SlideSelectHorizontal, args: ScrollerState): () => void {
  window.addEventListener('resize', debounce(updateStuff, 400));
  window.addEventListener('load', debounce(updateStuff, 400));
  const domObserver = observeDom(slider, updateStuff, {childList: true});
  
  return () => {
    window.removeEventListener('resize', updateStuff);
    window.removeEventListener('load', updateStuff);
    domObserver.disconnect();
  };
  
  function updateStuff() {
    args.items = toItemsPlacements(slider);
    args.width = slider.offsetWidth;
    args.half = slider.offsetWidth / 2;
    scrollToSelectedItem(slider, true);
  }
}

interface ScrollerState {
  items: ElWithLocation[],
  width: number,
  half: number,
  currElInFocus?: HTMLElement,
  isTouched: boolean,
  isScrolling: boolean,
  pendingSelectedItem?: ElWithLocation,
}

function toScrollHandler(
  slider: SlideSelectHorizontal,
  state: ScrollerState,
  debouncedFire: (el: ElWithLocation) => void,
  debouncedScrollToSelectedItem: () => void,
  debouncedMarkScrollingDone: () => void,
  mutateItem: ElementStyleMutatorOnScroll | undefined
) {
  return () => {
    const scroll = slider.scrollLeft;
    const scrollCenter = scroll + state.half;
    state.isScrolling = true;
    debouncedMarkScrollingDone();
    requestAnimationFrame(() => {
      let newElSelected = false;
      state.items.forEach(item => {
        const loc = item.location;
        if (loc > scroll - state.half && loc <= scrollCenter) {
          mutateItem?.(item.el, (loc - scroll) / state.half, 'left');
          if (isNewItemInFocus(state.currElInFocus as HTMLElement, item, scrollCenter)) {
            state.currElInFocus = item.el;
            fireSelectedIfNotTouchingOrScrolling(item);
            debouncedVibrate();
            newElSelected = true;
          }
        } else if (loc > scrollCenter && loc < scroll + state.width + state.half) {
          mutateItem?.(item.el, 1 - ((loc - scroll) - state.half) / state.half, 'right');
          if (isNewItemInFocus(state.currElInFocus as HTMLElement, item, scrollCenter)) {
            state.currElInFocus = item.el;
            fireSelectedIfNotTouchingOrScrolling(item);
            debouncedVibrate();
            newElSelected = true;
          }
        }
      });
      if (!newElSelected && !state.isTouched && !state.isScrolling) {
        debouncedScrollToSelectedItem();
      }
    });
    
    function fireSelectedIfNotTouchingOrScrolling(item: ElWithLocation): void {
      if (state.isTouched || state.isScrolling) {
        state.pendingSelectedItem = item;
      } else {
        fireSelectedItem(state, debouncedFire, item);
      }
    }
  };
}

function fireSelectedItem(state: ScrollerState, debouncedFire: (item: ElWithLocation) => void, item: ElWithLocation):void {
  debouncedFire(item);
  state.pendingSelectedItem = undefined;
}


function isNewItemInFocus(oldEl: HTMLElement, el: ElWithLocation, scrollCenter: number): boolean {
  return oldEl !== el.el
    && el.startsAt < scrollCenter && scrollCenter < el.endsAt;
}

function handleFiringEvent(item: ElWithLocation): void {
  fireEvent(item.el, scrollSettledEvent);
}

function scrollToSelectedItem(container: SlideSelectHorizontal, preventSmooth?: boolean): void {
  const candidate: HTMLElement | null = container.querySelector(`[${selectedItemAttr}]`);
  if (candidate) {
    container.scrollTo({
      left: candidate.offsetLeft + (candidate.offsetWidth / 2) - (container.offsetWidth / 2),
      behavior: preventSmooth ? 'auto' : 'smooth',
    });
  }
}

type ElementStyleMutatorOnScroll = (el: HTMLElement, ratio: number, where: 'left' | 'right') => void;

function toItemsPlacements(parent: HTMLElement): ElWithLocation[] {
  return Array.from(parent.childNodes).map(e => {
    const el = e as HTMLElement;
    return {
      el,
      location: el.offsetLeft + el.offsetWidth / 2,
      startsAt: el.offsetLeft,
      endsAt: el.offsetLeft + el.offsetWidth,
    };
  });
}

interface ElWithLocation {
  el: HTMLElement,
  location: number,
  startsAt: number, //left edge of element
  endsAt: number, //right edge of element
}

function toVibrateEffect(): () => void {
  return navigator.vibrate ?
    () => navigator.vibrate(2) :
    alwaysVoid;
}


